import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Tables } from '@limo/database'
import { expandZipSpec } from '@limo/shared/pricing'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'

type ZipFlatRateZone = Tables<'zip_flat_rate_zones'>

// Preset colors for zone selection (mirrors the polygon ZoneDialog palette)
const ZONE_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#F97316', // Orange
]

const zoneSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().max(500).optional(),
  zip_text: z.string().min(1, 'Enter at least one ZIP code'),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format'),
  is_active: z.boolean().default(true),
})

type ZoneFormData = z.infer<typeof zoneSchema>

interface ZipZoneDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  zone: ZipFlatRateZone | null
  accountId: string
}

export function ZipZoneDialog({
  open,
  onOpenChange,
  zone,
  accountId,
}: ZipZoneDialogProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const isEditing = !!zone

  const [zipText, setZipText] = useState('')

  const form = useForm<ZoneFormData>({
    resolver: zodResolver(zoneSchema),
    defaultValues: {
      name: '',
      description: '',
      zip_text: '',
      color: ZONE_COLORS[0],
      is_active: true,
    },
  })

  // Live preview of the expanded ZIP list
  const expanded = useMemo(() => expandZipSpec(zipText), [zipText])

  useEffect(() => {
    if (open) {
      if (zone) {
        const text = (zone.zip_codes ?? []).join(', ')
        setZipText(text)
        form.reset({
          name: zone.name,
          description: zone.description || '',
          zip_text: text,
          color: zone.color,
          is_active: zone.is_active,
        })
      } else {
        const randomColor = ZONE_COLORS[Math.floor(Math.random() * ZONE_COLORS.length)]
        setZipText('')
        form.reset({
          name: '',
          description: '',
          zip_text: '',
          color: randomColor,
          is_active: true,
        })
      }
    }
  }, [open, zone, form])

  const persist = async (data: ZoneFormData) => {
    const zipCodes = expandZipSpec(data.zip_text)
    if (zipCodes.length === 0) {
      throw new Error('No valid 5-digit ZIP codes found in the input.')
    }

    if (zone) {
      const { error } = await supabase
        .from('zip_flat_rate_zones')
        .update({
          name: data.name,
          description: data.description || null,
          zip_codes: zipCodes,
          color: data.color,
          is_active: data.is_active,
        })
        .eq('id', zone.id)
      if (error) throw error
    } else {
      const { error } = await supabase.from('zip_flat_rate_zones').insert({
        account_id: accountId,
        name: data.name,
        description: data.description || null,
        zip_codes: zipCodes,
        color: data.color,
        is_active: data.is_active,
      })
      if (error) throw error
    }
  }

  const mutation = useMutation({
    mutationFn: persist,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zip-flat-rate-zones', accountId] })
      queryClient.invalidateQueries({ queryKey: ['zip-flat-rate-rules', accountId] })
      toast({
        title: isEditing ? 'Zone updated' : 'Zone created',
        description: 'The ZIP zone has been saved successfully.',
      })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  const onSubmit = (data: ZoneFormData) => mutation.mutate(data)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit ZIP Zone' : 'Create ZIP Zone'}</DialogTitle>
          <DialogDescription>
            A ZIP zone is a named set of ZIP codes. Paste codes in any format —
            single, slash/comma lists, or ranges (e.g. 27601-27617).
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Buckhead, ATL Airport" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="zip_text"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ZIP Codes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="30305, 30309, 30326 or 27601-27617"
                      className="resize-none font-mono text-sm"
                      rows={4}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e)
                        setZipText(e.target.value)
                      }}
                    />
                  </FormControl>
                  <FormDescription>
                    {expanded.length > 0
                      ? `${expanded.length} ZIP code${expanded.length === 1 ? '' : 's'} detected`
                      : 'Enter ZIP codes separated by commas, slashes, or use a range'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe this service area..."
                      className="resize-none"
                      rows={2}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color</FormLabel>
                  <FormControl>
                    <div className="flex gap-2 flex-wrap">
                      {ZONE_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={`w-8 h-8 rounded-full border-2 transition-transform ${
                            field.value === color
                              ? 'border-foreground scale-110'
                              : 'border-transparent hover:scale-105'
                          }`}
                          style={{ backgroundColor: color }}
                          onClick={() => field.onChange(color)}
                        />
                      ))}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>
                      Inactive zones won't be used for matching
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditing ? 'Save Changes' : 'Create Zone'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
